import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Group,
  Title,
  Avatar,
  Menu,
  UnstyledButton,
  NavLink,
  Drawer,
  Text,
  Divider,
  Stack,
  Collapse,
  ScrollArea,
  TextInput,
  Box,
  Badge,
} from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import {
  IconLogout,
  IconMap,
  IconEdit,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconCloud,
  IconSearch,
  IconX,
  IconServer,
  IconBulb,
  IconRobot,
  IconHeartRateMonitor,
  IconClipboardData,
  IconDatabaseImport,
  IconShieldCheck,
  IconGitCompare,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { SitemapContent } from '../SitemapDrawer';
import { DraggableSitemapButton } from '../DraggableSitemapButton';
import { EnvironmentSwitcher } from '../EnvironmentSwitcher';
import { ColorSchemeToggle } from './ColorSchemeToggle';
import {
  getNavigationItemsByCategory,
} from '../../constants/navigation';
import { prefetchRoute } from '../../lib/routePreloader';
import logo from '../../assets/images/logo-primary-collapsed.svg';
import { useState, useMemo } from 'react';
import { useEnvironment } from '../../hooks/useEnvironment';
import './styles.css';

const managementItems = getNavigationItemsByCategory('management');
const dataHygieneItems = getNavigationItemsByCategory('data-hygiene');
const monitoringItems = getNavigationItemsByCategory('monitoring');
const infrastructureItems = getNavigationItemsByCategory('infrastructure');
const onPremItems = getNavigationItemsByCategory('onprem');
const insightsItems = getNavigationItemsByCategory('insights');
const agentItems = getNavigationItemsByCategory('agent');
const healthItems = getNavigationItemsByCategory('health');
const auditItems = getNavigationItemsByCategory('console-audit');
const backfillItems = getNavigationItemsByCategory('backfill');
const makerCheckerItems = getNavigationItemsByCategory('maker-checker');

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isOnPremAllowed } = useEnvironment();
  const [opened, { toggle }] = useDisclosure();
  const [sitemapOpened, { open: openSitemap, close: closeSitemap }] = useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Persist collapsible state in localStorage
  const [managementOpened, setManagementOpened] = useLocalStorage({
    key: 'nav-management-opened',
    defaultValue: false,
  });
  const [monitoringOpened, setMonitoringOpened] = useLocalStorage({
    key: 'nav-monitoring-opened',
    defaultValue: false,
  });
  const [infrastructureOpened, setInfrastructureOpened] = useLocalStorage({
    key: 'nav-infrastructure-opened',
    defaultValue: false,
  });
  const [onPremOpened, setOnPremOpened] = useLocalStorage({
    key: 'nav-onprem-opened',
    defaultValue: false,
  });
  const [insightsOpened, setInsightsOpened] = useLocalStorage({
    key: 'nav-insights-opened',
    defaultValue: false,
  });
  const [agentOpened, setAgentOpened] = useLocalStorage({
    key: 'nav-agent-opened',
    defaultValue: false,
  });
  const [healthOpened, setHealthOpened] = useLocalStorage({
    key: 'nav-health-opened',
    defaultValue: false,
  });
  const [auditOpened, setAuditOpened] = useLocalStorage({
    key: 'nav-audit-opened',
    defaultValue: false,
  });
  const [backfillOpened, setBackfillOpened] = useLocalStorage({
    key: 'nav-backfill-opened',
    defaultValue: false,
  });
  const [dataHygieneOpened, setDataHygieneOpened] = useLocalStorage({
    key: 'nav-data-hygiene-opened',
    defaultValue: false,
  });
  const [makerCheckerOpened, setMakerCheckerOpened] = useLocalStorage({
    key: 'nav-maker-checker-opened',
    defaultValue: false,
  });

  // Accordion behavior: when one opens, close others
  const handleManagementToggle = () => {
    if (!managementOpened) {
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setManagementOpened(!managementOpened);
  };

  const handleMonitoringToggle = () => {
    if (!monitoringOpened) {
      setManagementOpened(false);
      setInfrastructureOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setMonitoringOpened(!monitoringOpened);
  };

  const handleInfrastructureToggle = () => {
    if (!infrastructureOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setInfrastructureOpened(!infrastructureOpened);
  };

  const handleOnPremToggle = () => {
    if (!onPremOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setOnPremOpened(!onPremOpened);
  };

  const handleInsightsToggle = () => {
    if (!insightsOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setInsightsOpened(!insightsOpened);
  };

  const handleAgentToggle = () => {
    if (!agentOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setAgentOpened(!agentOpened);
  };

  const handleHealthToggle = () => {
    if (!healthOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setHealthOpened(!healthOpened);
  };

  const handleAuditToggle = () => {
    if (!auditOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setAuditOpened(!auditOpened);
  };

  const handleBackfillToggle = () => {
    if (!backfillOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setDataHygieneOpened(false);
      setMakerCheckerOpened(false);
    }
    setBackfillOpened(!backfillOpened);
  };

  const handleDataHygieneToggle = () => {
    if (!dataHygieneOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setMakerCheckerOpened(false);
    }
    setDataHygieneOpened(!dataHygieneOpened);
  };

  const handleMakerCheckerToggle = () => {
    if (!makerCheckerOpened) {
      setManagementOpened(false);
      setMonitoringOpened(false);
      setInfrastructureOpened(false);
      setOnPremOpened(false);
      setInsightsOpened(false);
      setAgentOpened(false);
      setHealthOpened(false);
      setAuditOpened(false);
      setBackfillOpened(false);
      setDataHygieneOpened(false);
    }
    setMakerCheckerOpened(!makerCheckerOpened);
  };


  const handleLogout = () => {
    logout();
  };

  const getUserInitial = () => {
    return user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    return user?.name || user?.email || 'User';
  };

  const getUserEmail = () => {
    return user?.email || '';
  };

  // Filter navigation items based on search query
  const filteredManagementItems = useMemo(() => {
    if (!searchQuery) return managementItems;
    const query = searchQuery.toLowerCase();
    return managementItems.filter(item => 
      item.text.toLowerCase().includes(query) || 
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredMonitoringItems = useMemo(() => {
    if (!searchQuery) return monitoringItems;
    const query = searchQuery.toLowerCase();
    return monitoringItems.filter(item => 
      item.text.toLowerCase().includes(query) || 
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredInfrastructureItems = useMemo(() => {
    if (!searchQuery) return infrastructureItems;
    const query = searchQuery.toLowerCase();
    return infrastructureItems.filter(item => 
      item.text.toLowerCase().includes(query) || 
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredOnPremItems = useMemo(() => {
    if (!searchQuery) return onPremItems;
    const query = searchQuery.toLowerCase();
    return onPremItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredInsightsItems = useMemo(() => {
    if (!searchQuery) return insightsItems;
    const query = searchQuery.toLowerCase();
    return insightsItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredAgentItems = useMemo(() => {
    if (!searchQuery) return agentItems;
    const query = searchQuery.toLowerCase();
    return agentItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Show sections if they have items (always show when no search, or show if filtered results exist)
  const shouldShowManagement = !searchQuery || filteredManagementItems.length > 0;
  const shouldShowMonitoring = !searchQuery || filteredMonitoringItems.length > 0;
  const shouldShowInfrastructure = !searchQuery || filteredInfrastructureItems.length > 0;
  const shouldShowOnPrem = isOnPremAllowed && (!searchQuery || filteredOnPremItems.length > 0);
  const shouldShowInsights = !searchQuery || filteredInsightsItems.length > 0;
  const shouldShowAgent = !searchQuery || filteredAgentItems.length > 0;

  const filteredHealthItems = useMemo(() => {
    if (!searchQuery) return healthItems;
    const query = searchQuery.toLowerCase();
    return healthItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const shouldShowHealth = !searchQuery || filteredHealthItems.length > 0;

  const filteredAuditItems = useMemo(() => {
    if (!searchQuery) return auditItems;
    const query = searchQuery.toLowerCase();
    return auditItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const shouldShowAudit = !searchQuery || filteredAuditItems.length > 0;

  const filteredBackfillItems = useMemo(() => {
    if (!searchQuery) return backfillItems;
    const query = searchQuery.toLowerCase();
    return backfillItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const shouldShowBackfill = !searchQuery || filteredBackfillItems.length > 0;

  const filteredDataHygieneItems = useMemo(() => {
    if (!searchQuery) return dataHygieneItems;
    const query = searchQuery.toLowerCase();
    return dataHygieneItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const shouldShowDataHygiene = !searchQuery || filteredDataHygieneItems.length > 0;

  const filteredMakerCheckerItems = useMemo(() => {
    if (!searchQuery) return makerCheckerItems;
    const query = searchQuery.toLowerCase();
    return makerCheckerItems.filter(item =>
      item.text.toLowerCase().includes(query) ||
      item.path.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const shouldShowMakerChecker = !searchQuery || filteredMakerCheckerItems.length > 0;

  // Clear search when clicking outside or navigating
  const handleNavigation = (path: string) => {
    navigate(path);
    closeSitemap();
    setSearchQuery('');
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 240,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <img 
              src={logo} 
              alt="Admin Console Logo" 
              style={{ height: 40, cursor: 'pointer' }}
              onClick={() => handleNavigation('/organizations')}
              role="button"
              tabIndex={0}
              aria-label="Navigate to organizations"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNavigation('/organizations');
                }
              }}
            />
            <Title
              order={3}
              className="header-title"
              onClick={() => handleNavigation('/organizations')}
              role="button"
              tabIndex={0}
              aria-label="Navigate to organizations"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNavigation('/organizations');
                }
              }}
            >
              Console
            </Title>
          </Group>

          <Group gap="md">
            <EnvironmentSwitcher />
            <ColorSchemeToggle />
            <Menu shadow="md" width={240}>
              <Menu.Target>
                <UnstyledButton>
                  <Avatar 
                    src={user?.picture} 
                    color="violet" 
                    radius="xl" 
                    size="md"
                  >
                    {getUserInitial()}
                  </Avatar>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>
                      {getUserDisplayName()}
                    </Text>
                    {getUserEmail() && (
                      <Text size="xs" c="dimmed">
                        {getUserEmail()}
                      </Text>
                    )}
                  </Stack>
                </Menu.Label>
                <Divider />
<Menu.Item
                   leftSection={<IconLogout size={16} />}
                   onClick={handleLogout}
                   aria-label="Sign out of your account"
                   role="button"
                   tabIndex={0}
                 >
                   Sign Out
                 </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" role="navigation" aria-label="Main navigation">
        <ScrollArea h="calc(100vh - 60px)" type="scroll">
          <Stack gap="md">
            {/* Search Bar */}
            <TextInput
              placeholder="Search navigation..."
              leftSection={<IconSearch size={16} />}
              rightSection={
                searchQuery ? (
                  <UnstyledButton
                    onClick={() => setSearchQuery('')}
                    style={{ display: 'flex', alignItems: 'center' }}
                    aria-label="Clear search"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSearchQuery('');
                      }
                    }}
                  >
                    <IconX size={16} />
                  </UnstyledButton>
                ) : null
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              size="sm"
              aria-label="Search navigation menu"
              role="searchbox"
              styles={{
                input: {
                  borderRadius: '8px',
                  outline: 'none',
                  boxShadow: 'none',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  '&:focus, &[data-focus]': {
                    outline: 'none',
                    boxShadow: 'none',
                    borderColor: 'var(--mantine-color-violet-5)',
                  },
                },
              }}
            />

            <Stack gap="xs">
              {/* Management Section */}
              {shouldShowManagement && (
                <>
                  <UnstyledButton
                    onClick={handleManagementToggle}
                    className="nav-category-header"
                    data-category="management"
                    aria-label="Toggle Management section"
                    aria-expanded={managementOpened || (!!searchQuery && filteredManagementItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleManagementToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {managementOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconEdit size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-blue-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Management
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredManagementItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={managementOpened || (!!searchQuery && filteredManagementItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredManagementItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Monitoring Section */}
              {shouldShowMonitoring && (
                <>
                  {shouldShowManagement && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleMonitoringToggle}
                    className="nav-category-header"
                    data-category="monitoring"
                    aria-label="Toggle Monitoring section"
                    aria-expanded={monitoringOpened || (!!searchQuery && filteredMonitoringItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMonitoringToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {monitoringOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconChartBar size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-violet-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Monitoring
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredMonitoringItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={monitoringOpened || (!!searchQuery && filteredMonitoringItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredMonitoringItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Infrastructure Section */}
              {shouldShowInfrastructure && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleInfrastructureToggle}
                    className="nav-category-header"
                    data-category="infrastructure"
                    aria-label="Toggle Infrastructure section"
                    aria-expanded={infrastructureOpened || (!!searchQuery && filteredInfrastructureItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleInfrastructureToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {infrastructureOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconCloud size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-green-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Infrastructure
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredInfrastructureItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={infrastructureOpened || (!!searchQuery && filteredInfrastructureItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredInfrastructureItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* On-Prem Section */}
              {shouldShowOnPrem && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleOnPremToggle}
                    className="nav-category-header"
                    data-category="onprem"
                    aria-label="Toggle On-Prem section"
                    aria-expanded={onPremOpened || (!!searchQuery && filteredOnPremItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOnPremToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {onPremOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconServer size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-blue-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        On-Prem
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredOnPremItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={onPremOpened || (!!searchQuery && filteredOnPremItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredOnPremItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Insights Section */}
              {shouldShowInsights && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleInsightsToggle}
                    className="nav-category-header"
                    data-category="insights"
                    aria-label="Toggle Insights section"
                    aria-expanded={insightsOpened || (!!searchQuery && filteredInsightsItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleInsightsToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {insightsOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconBulb size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-yellow-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Insights
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredInsightsItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={insightsOpened || (!!searchQuery && filteredInsightsItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredInsightsItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Hivel Agent Section */}
              {shouldShowAgent && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleAgentToggle}
                    className="nav-category-header"
                    data-category="agent"
                    aria-label="Toggle Hivel Agent section"
                    aria-expanded={agentOpened || (!!searchQuery && filteredAgentItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAgentToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {agentOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconRobot size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-orange-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Hivel Agent
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredAgentItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={agentOpened || (!!searchQuery && filteredAgentItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredAgentItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Health Dashboard Section */}
              {shouldShowHealth && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights || shouldShowAgent) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleHealthToggle}
                    className="nav-category-header"
                    data-category="health"
                    aria-label="Toggle Health Dashboard section"
                    aria-expanded={healthOpened || (!!searchQuery && filteredHealthItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleHealthToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {healthOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconHeartRateMonitor size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-pink-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Health Dashboard
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredHealthItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={healthOpened || (!!searchQuery && filteredHealthItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredHealthItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Console Audit Section */}
              {shouldShowAudit && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights || shouldShowAgent || shouldShowHealth) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleAuditToggle}
                    className="nav-category-header"
                    data-category="console-audit"
                    aria-label="Toggle Console Audit section"
                    aria-expanded={auditOpened || (!!searchQuery && filteredAuditItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAuditToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {auditOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconClipboardData size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-teal-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Console Audit
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredAuditItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={auditOpened || (!!searchQuery && filteredAuditItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredAuditItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Backfill Section */}
              {shouldShowBackfill && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights || shouldShowAgent || shouldShowHealth || shouldShowAudit) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleBackfillToggle}
                    className="nav-category-header"
                    data-category="backfill"
                    aria-label="Toggle Backfill section"
                    aria-expanded={backfillOpened || (!!searchQuery && filteredBackfillItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleBackfillToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {backfillOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconDatabaseImport size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-indigo-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Backfill
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredBackfillItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={backfillOpened || (!!searchQuery && filteredBackfillItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredBackfillItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Data Hygiene Section */}
              {shouldShowDataHygiene && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights || shouldShowAgent || shouldShowHealth || shouldShowAudit || shouldShowBackfill) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleDataHygieneToggle}
                    className="nav-category-header"
                    data-category="data-hygiene"
                    aria-label="Toggle Data Hygiene section"
                    aria-expanded={dataHygieneOpened || (!!searchQuery && filteredDataHygieneItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDataHygieneToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {dataHygieneOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconShieldCheck size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-cyan-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Data Hygiene
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredDataHygieneItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={dataHygieneOpened || (!!searchQuery && filteredDataHygieneItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredDataHygieneItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* Maker Checker Section */}
              {shouldShowMakerChecker && (
                <>
                  {(shouldShowManagement || shouldShowMonitoring || shouldShowInfrastructure || shouldShowOnPrem || shouldShowInsights || shouldShowAgent || shouldShowHealth || shouldShowAudit || shouldShowBackfill || shouldShowDataHygiene) && <Divider my="xs" variant="dashed" />}
                  <UnstyledButton
                    onClick={handleMakerCheckerToggle}
                    className="nav-category-header"
                    data-category="maker-checker"
                    aria-label="Toggle Maker Checker section"
                    aria-expanded={makerCheckerOpened || (!!searchQuery && filteredMakerCheckerItems.length > 0)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMakerCheckerToggle();
                      }
                    }}
                  >
                    <Group gap={8}>
                      {makerCheckerOpened ? (
                        <IconChevronDown size={16} className="nav-chevron" />
                      ) : (
                        <IconChevronRight size={16} className="nav-chevron" />
                      )}
                      <IconGitCompare size={18} className="nav-category-icon" style={{ color: 'var(--mantine-color-grape-6)' }} />
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', flex: 1 }}>
                        Maker Checker
                      </Text>
                      {searchQuery && (
                        <Badge size="xs" variant="light" radius="xl">
                          {filteredMakerCheckerItems.length}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={makerCheckerOpened || (!!searchQuery && filteredMakerCheckerItems.length > 0)} transitionDuration={200} transitionTimingFunction="ease">
                    <Stack gap={2} pl={26}>
                      {filteredMakerCheckerItems.map((item: any) => (
                        <NavLink
                          key={item.path}
                          label={<Text size="xs">{item.text}</Text>}
                          leftSection={<item.icon size={15} />}
                          active={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          onMouseEnter={() => prefetchRoute(item.path)}
                          onFocus={() => prefetchRoute(item.path)}
                          className="nav-link"
                          aria-label={`Navigate to ${item.text}`}
                          role="button"
                          tabIndex={0}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}

              {/* No results message */}
              {searchQuery && !shouldShowManagement && !shouldShowMonitoring && !shouldShowInfrastructure && !shouldShowOnPrem && !shouldShowInsights && !shouldShowAgent && !shouldShowAudit && !shouldShowHealth && !shouldShowBackfill && !shouldShowDataHygiene && !shouldShowMakerChecker && (
                <Box p="md" ta="center">
                  <Text size="sm" c="dimmed">
                    No results found for "{searchQuery}"
                  </Text>
                </Box>
              )}
            </Stack>
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>

      {/* Draggable Sitemap Button - Outside AppShell to ensure proper positioning */}
      <DraggableSitemapButton onClick={openSitemap} />

      {/* Sitemap Drawer */}
      <Drawer
        opened={sitemapOpened}
        onClose={closeSitemap}
        title={
          <Group gap="sm">
            <IconMap size={24} />
            <Title order={3}>Sitemap</Title>
          </Group>
        }
        position="right"
        size="xl"
        padding="lg"
        overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      >
        <SitemapContent onNavigate={handleNavigation} compact />
      </Drawer>
    </AppShell>
  );
}

